/**
 * Lead Volume — the inflow-rate surface on the Dashboard's Volume sub-tab.
 *
 * Wire shape for GET /api/lead-volume. Three time-scale arrays — daily,
 * weekly, monthly — each emitted right-to-left from today (PT):
 *   - daily: 14 entries ending today (today and the 13 prior calendar days).
 *   - weekly: 8 trailing 7-day windows ending today (NOT calendar weeks).
 *     The current window is `today−6 … today`; the prior is
 *     `today−13 … today−7`; etc. weekStart/weekEnd are inclusive YYYY-MM-DD.
 *   - monthly: 12 calendar months ending in the current month. month is
 *     YYYY-MM.
 *
 * By construction the LAST element of each array is the in-progress bucket
 * (today / current trailing-week window / current calendar month). The web
 * styles it at reduced opacity and labels it "in progress" off `array.length
 * - 1` — no per-bucket flag here.
 *
 * Empty days/weeks/months emit explicit `count: 0` so every chart always has
 * its full bar count; the web never has to fill gaps.
 *
 * Alongside the buckets the wire carries a `kpis` block — Today / WTD / MTD
 * counts plus comparisons — so the page can lead with "are we okay?" instead
 * of making the operator read it off the bars. Comparisons are nullable when
 * the look-back falls outside the fetch window (early in system life).
 *
 * Anchored on `Leads.Creation Date` (audited 2026-06-05 — 99.7% populated,
 * organic monthly shape; the intrinsic `createdTime` is migration-tainted and
 * NOT used).
 */

import { z } from "zod";

export const VolumeDailyBucketSchema = z.object({
  date: z.string(),
  count: z.number().int().nonnegative(),
});

export const VolumeWeeklyBucketSchema = z.object({
  weekStart: z.string(),
  weekEnd: z.string(),
  count: z.number().int().nonnegative(),
});

export const VolumeMonthlyBucketSchema = z.object({
  month: z.string(),
  count: z.number().int().nonnegative(),
});

export const VolumeKpiTodaySchema = z.object({
  count: z.number().int().nonnegative(),
  comparison: z.number().nonnegative().nullable(),
});

export const VolumeKpiWtdSchema = z.object({
  count: z.number().int().nonnegative(),
  comparison: z.number().int().nonnegative().nullable(),
});

export const VolumeKpiMtdSchema = z.object({
  count: z.number().int().nonnegative(),
  projection: z.number().nonnegative(),
  lastMonthTotal: z.number().int().nonnegative().nullable(),
});

export const VolumeKpisSchema = z.object({
  today: VolumeKpiTodaySchema,
  wtd: VolumeKpiWtdSchema,
  mtd: VolumeKpiMtdSchema,
});

export const VolumeResponseSchema = z.object({
  daily: z.array(VolumeDailyBucketSchema),
  weekly: z.array(VolumeWeeklyBucketSchema),
  monthly: z.array(VolumeMonthlyBucketSchema),
  kpis: VolumeKpisSchema,
});

export type VolumeDailyBucket = z.infer<typeof VolumeDailyBucketSchema>;
export type VolumeWeeklyBucket = z.infer<typeof VolumeWeeklyBucketSchema>;
export type VolumeMonthlyBucket = z.infer<typeof VolumeMonthlyBucketSchema>;
export type VolumeKpiToday = z.infer<typeof VolumeKpiTodaySchema>;
export type VolumeKpiWtd = z.infer<typeof VolumeKpiWtdSchema>;
export type VolumeKpiMtd = z.infer<typeof VolumeKpiMtdSchema>;
export type VolumeKpis = z.infer<typeof VolumeKpisSchema>;
export type VolumeResponse = z.infer<typeof VolumeResponseSchema>;
